import React from 'react';

import autoBind from 'react-autobind';
import { If, Then } from 'react-if';
import ReactAudioPlayer from 'react-audio-player';

import { Button, Icon, CircularProgress } from '@mui/material';
import { red } from '@mui/material/colors';

import Localization from '../Localization.jsx';
import { encodeAudioBlobToMp3File } from '../utils/audioMp3.js';

// Candidate container formats for MediaRecorder, in order of preference. The
// recorded blob is transcoded to MP3 afterwards, so the container only needs to
// be decodable by the Web Audio API (it always is for whatever the browser can
// record).
const RECORDER_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
];

const DEFAULT_MAX_DURATION_MS = 5 * 60 * 1000; // 5 minutes

function pickSupportedMimeType() {
  if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') {
    return '';
  }
  for (let i = 0; i < RECORDER_MIME_CANDIDATES.length; i++) {
    const candidate = RECORDER_MIME_CANDIDATES[i];
    if (window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported(candidate)) {
      return candidate;
    }
  }
  return '';
}

class AudioRecorder extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      // 'idle' | 'recording' | 'encoding' | 'preview'
      mode: 'idle',
      elapsedMs: 0,
      previewUrl: '',
      recordedFile: null,
    };

    // non-render instance state
    this.mediaRecorder = null;
    this.mediaStream = null;
    this.recordedChunks = [];
    this.timerId = null;
    this.startTimestamp = 0;
    this._isMounted = true;

    autoBind(this);
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.cleanupStream();
    this.clearTimer();
    this.revokePreview();
  }

  isSupported() {
    return (
      typeof navigator !== 'undefined' &&
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function' &&
      typeof window !== 'undefined' &&
      typeof window.MediaRecorder !== 'undefined'
    );
  }

  getMaxDurationMs() {
    return this.props.maxDurationMs ? this.props.maxDurationMs : DEFAULT_MAX_DURATION_MS;
  }

  clearTimer() {
    if (this.timerId != null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  revokePreview() {
    if (this.state.previewUrl) {
      try {
        window.URL.revokeObjectURL(this.state.previewUrl);
      } catch (error) {
        console.log(error);
      }
    }
  }

  cleanupStream() {
    if (this.mediaStream) {
      try {
        this.mediaStream.getTracks().forEach((track) => track.stop());
      } catch (error) {
        console.log(error);
      }
      this.mediaStream = null;
    }
    this.mediaRecorder = null;
  }

  notify(message, success) {
    if (this.props.root && typeof this.props.root.addNotification === 'function') {
      this.props.root.addNotification(message, success);
    }
  }

  async startRecording() {
    if (!this.isSupported()) {
      this.notify(Localization.strings.RECORDING_NOT_SUPPORTED, false);
      return;
    }

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
    } catch (error) {
      console.log(error);
      this.notify(Localization.strings.MICROPHONE_ACCESS_DENIED, false);
      return;
    }

    this.recordedChunks = [];
    const mimeType = pickSupportedMimeType();
    try {
      this.mediaRecorder = mimeType
        ? new window.MediaRecorder(this.mediaStream, { mimeType })
        : new window.MediaRecorder(this.mediaStream);
    } catch (error) {
      console.log(error);
      this.cleanupStream();
      this.notify(Localization.strings.RECORDING_NOT_SUPPORTED, false);
      return;
    }

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        this.recordedChunks.push(event.data);
      }
    };
    this.mediaRecorder.onstop = this.handleRecorderStop;

    this.mediaRecorder.start();
    this.startTimestamp = Date.now();
    this.setState({ mode: 'recording', elapsedMs: 0 });
    this.clearTimer();
    this.timerId = setInterval(this.tick, 200);
  }

  tick() {
    const elapsedMs = Date.now() - this.startTimestamp;
    this.setState({ elapsedMs });
    if (elapsedMs >= this.getMaxDurationMs()) {
      this.notify(Localization.strings.RECORDING_MAX_LENGTH_REACHED, false);
      this.stopRecording();
    }
  }

  stopRecording() {
    this.clearTimer();
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.stop();
      } catch (error) {
        console.log(error);
      }
    }
  }

  async handleRecorderStop() {
    // Tracks are no longer needed once recording has stopped.
    const recorderMimeType = this.mediaRecorder ? this.mediaRecorder.mimeType : '';
    this.cleanupStream();

    if (this.recordedChunks.length === 0) {
      this.setState({ mode: 'idle', elapsedMs: 0 });
      return;
    }

    this.setState({ mode: 'encoding' });
    const recordedBlob = new Blob(this.recordedChunks, {
      type: recorderMimeType || 'audio/webm',
    });

    try {
      const fileName = 'recording_' + Date.now() + '.mp3';
      const mp3File = await encodeAudioBlobToMp3File(recordedBlob, fileName);
      const previewUrl = window.URL.createObjectURL(mp3File);
      if (!this._isMounted) {
        // Component unmounted while MP3 encoding was in flight: revoke the object URL we just
        // created (componentWillUnmount already ran and could not see it) and skip setState.
        window.URL.revokeObjectURL(previewUrl);
        return;
      }
      this.setState({
        mode: 'preview',
        recordedFile: mp3File,
        previewUrl,
      });
    } catch (error) {
      console.log(error);
      this.notify(Localization.strings.RECORDING_FAILED, false);
      this.setState({ mode: 'idle', elapsedMs: 0 });
    }
  }

  useRecording() {
    if (this.state.recordedFile && typeof this.props.onRecorded === 'function') {
      this.props.onRecorded(this.state.recordedFile);
    }
    // The uploaded file is owned by the parent now; reset our local state.
    this.revokePreview();
    this.setState({
      mode: 'idle',
      elapsedMs: 0,
      previewUrl: '',
      recordedFile: null,
    });
  }

  reRecord() {
    this.revokePreview();
    this.setState({
      mode: 'idle',
      elapsedMs: 0,
      previewUrl: '',
      recordedFile: null,
    });
  }

  formatTime(ms) {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const pad = (n) => (n < 10 ? '0' + n : '' + n);
    return pad(minutes) + ':' + pad(seconds);
  }

  render() {
    const containerStyle = {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
    };

    if (!this.isSupported()) {
      return (
        <div style={containerStyle}>
          <p style={{ fontWeight: 'bold', padding: '0 10px' }}>
            {Localization.strings.RECORDING_NOT_SUPPORTED}
          </p>
        </div>
      );
    }

    return (
      <div style={containerStyle}>
        <If condition={this.state.mode === 'idle'}>
          <Then>
            <div>
              <Button
                variant="contained"
                color="primary"
                onClick={this.startRecording}
              >
                <Icon className="buttonIconLeft">mic</Icon>
                {Localization.strings.START_RECORDING}
              </Button>
            </div>
          </Then>
        </If>

        <If condition={this.state.mode === 'recording'}>
          <Then>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon style={{ color: red[500], marginRight: '8px' }}>fiber_manual_record</Icon>
                <span style={{ fontSize: '1.4rem', fontVariantNumeric: 'tabular-nums' }}>
                  {this.formatTime(this.state.elapsedMs)}
                </span>
              </div>
              <div style={{ marginTop: '8px' }}>
                <Button variant="contained" color="secondary" onClick={this.stopRecording}>
                  <Icon className="buttonIconLeft">stop</Icon>
                  {Localization.strings.STOP_RECORDING}
                </Button>
              </div>
            </div>
          </Then>
        </If>

        <If condition={this.state.mode === 'encoding'}>
          <Then>
            <div>
              <CircularProgress variant="indeterminate" />
              <br />
              {Localization.strings.PROCESSING_RECORDING + '...'}
            </div>
          </Then>
        </If>

        <If condition={this.state.mode === 'preview'}>
          <Then>
            <div style={{ width: '100%' }}>
              <ReactAudioPlayer
                src={this.state.previewUrl}
                controls
                style={{ display: 'block', margin: '0 auto 8px auto' }}
              />
              <div>
                <Button variant="contained" color="primary" onClick={this.useRecording}>
                  <Icon className="buttonIconLeft">check</Icon>
                  {Localization.strings.USE_RECORDING}
                </Button>
                <Button color="primary" onClick={this.reRecord} style={{ marginLeft: '8px' }}>
                  <Icon className="buttonIconLeft">replay</Icon>
                  {Localization.strings.RE_RECORD}
                </Button>
              </div>
            </div>
          </Then>
        </If>
      </div>
    );
  }
}

AudioRecorder.defaultProps = {
  onRecorded: null,
  root: null,
  maxDurationMs: DEFAULT_MAX_DURATION_MS,
};

export default AudioRecorder;
